import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseChartDirective } from 'ng2-charts';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CustomerStatsCardComponent } from '../../../components/customer-stats-card/customer-stats-card.component';
import { DashboardStatData } from '../../../shared/types';
import { MobileDetectionMixin } from '../../../shared/mixins/mobile-detection.mixin';
import {
  DateRange,
  DateRangePickerComponent,
} from '../../../shared/components/date-range-picker/date-range-picker.component';

/**
 * Car wash dashboard tab component — displays wash counts, charts and location/period filters for car wash analytics.
 * @component
 * @selector app-car-wash-tab
 * @example
 *   <app-car-wash-tab [selectedCarWashPeriod]="selectedPeriod" (selectCarWashPeriod)="onPeriodSelect($event)"></app-car-wash-tab>
 * @input {string} selectedCarWashPeriod - Currently selected time period key for the car wash data (e.g., '24h', '7d').
 * @input {boolean} isCarWashDropdownOpen - Whether the car wash period dropdown is open.
 * @input {Array<{value: string; label: string}>} periodOptions - Available period options to choose from.
 * @input {DashboardStatData[]} carWashDashboardStats - Summary statistic cards data for the car wash tab.
 * @input {any} washesByHourData - Chart data object for washes grouped by hour.
 * @input {any} washesByHourOptions - Chart configuration options for the washes-by-hour chart.
 * @input {any} washesByServiceTypeData - Chart data object for washes grouped by service type.
 * @input {any} washesByServiceTypeOptions - Chart configuration options for the washes-by-service-type chart.
 * @input {any} carWashStatsData - Additional raw stats data for the car wash tab.
 * @input {string} selectedLocation - Currently selected location id for filtering.
 * @input {string} selectedLocationLabel - Human readable label for the selected location.
 * @input {boolean} isLocationDropdownOpen - Whether the location dropdown is open.
 * @input {Array<{value: string; label: string}>} locationOptions - Available locations to filter the data.
 * @input {boolean} isInitialLoad - True when the component is rendering for the first time (affects empty state display).
 * @input {boolean} hasAppliedFilters - True when the user has applied filters (affects empty state and chart display).
 * @output {EventEmitter<void>} toggleCarWashDropdown - Emitted to request toggling the car wash period dropdown UI.
 * @output {EventEmitter<string>} selectCarWashPeriod - Emitted when a car wash period is selected; payload is the period key.
 * @output {EventEmitter<void>} toggleLocationDropdown - Emitted to request toggling the location dropdown UI.
 * @output {EventEmitter<string>} selectLocation - Emitted when a location is selected; payload is the location id.
 *
 * Key methods and getters:
 * - hasWashesByHourData(): boolean — Returns true if the washes-by-hour dataset exists and contains any non-zero values.
 * - isWashesByHourDataEmpty(): boolean — Returns true if the washes-by-hour dataset is missing or all zeros.
 * - hasWashesByServiceTypeData(): boolean — Returns true if the washes-by-service-type dataset exists and contains any non-zero values.
 * - isWashesByServiceTypeDataEmpty(): boolean — Returns true if the washes-by-service-type dataset is missing or all zeros.
 * - washesByServiceTypeLegendItems: Array<{label: string; color: string; value?: number}> — Derives legend items (label, color, value) from the service-type chart data.
 * - totalWashes: number — Computes the total washes by summing values in the service-type dataset.
 * - isCarWashStatsDataEmpty(): boolean — Determines if the dashboard stat cards are empty or zero-valued.
 * - isAllDataEmpty(): boolean — Aggregates chart emptiness checks to determine if all visual data is empty.
 * - shouldShowEmptyState(): boolean — Determines whether to show the global empty state (depends on initial load and applied filters).
 * - shouldShowIndividualEmptyStates(): boolean — Determines whether to show per-chart empty states (depends on filters/initial load).
 *
 * Lifecycle hooks:
 * - This component relies on input changes provided by its parent; no explicit Angular lifecycle hooks (ngOnInit/ngOnChanges) are implemented in this class file. Any initialization or side effects should be handled by the parent or mixins applied to the component.
 */
@Component({
  selector: 'app-car-wash-tab',
  standalone: true,
  imports: [
    CommonModule,
    BaseChartDirective,
    CustomerStatsCardComponent,
    MatTooltipModule,
    DateRangePickerComponent,
  ],
  templateUrl: './car-wash-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../../dashboard.component.scss',
})
export class CarWashTabComponent extends MobileDetectionMixin {
  @Input() selectedCarWashPeriod!: string;
  @Input() selectedPeriodLabel = '';
  @Input() isCarWashDropdownOpen = false;
  @Input() periodOptions: { value: string; label: string }[] = [];
  @Input() customDateRange: DateRange = { startDate: null, endDate: null };
  @Input() carWashDashboardStats: DashboardStatData[] = [];

  @Input() washesByHourData: any;
  @Input() washesByHourOptions: any;
  @Input() washesByServiceTypeData: any;
  @Input() washesByServiceTypeOptions: any;
  @Input() carWashStatsData: any;

  // Location dropdown (shared pattern with other tabs)
  @Input() selectedLocation = '';
  @Input() selectedLocationLabel = '';
  @Input() isLocationDropdownOpen = false;
  @Input() locationOptions: { value: string; label: string }[] = [];
  @Input() isInitialLoad = true; // Track if this is initial page load
  @Input() hasAppliedFilters = false; // Track if filters have been applied

  @Output() toggleCarWashDropdown = new EventEmitter<void>();
  @Output() selectCarWashPeriod = new EventEmitter<string>();
  @Output() dateRangeApply = new EventEmitter<{ period: string; customRange?: DateRange }>();
  @Output() closePeriodDropdown = new EventEmitter<void>();

  @Output() toggleLocationDropdown = new EventEmitter<void>();
  @Output() selectLocation = new EventEmitter<string>();

  // Helper methods for Washes by Hour chart
  hasWashesByHourData(): boolean {
    const data = this.washesByHourData?.datasets?.[0]?.data;
    return data && data.length > 0 && !data.every((value: number) => value === 0);
  }

  isWashesByHourDataEmpty(): boolean {
    const data = this.washesByHourData?.datasets?.[0]?.data;
    return !data || data.length === 0 || data.every((value: number) => value === 0);
  }

  // Helper methods for Washes by Service Type chart
  hasWashesByServiceTypeData(): boolean {
    const data = this.washesByServiceTypeData?.datasets?.[0]?.data;
    return data && data.length > 0 && !data.every((value: number) => value === 0);
  }

  isWashesByServiceTypeDataEmpty(): boolean {
    const data = this.washesByServiceTypeData?.datasets?.[0]?.data;
    return !data || data.length === 0 || data.every((value: number) => value === 0);
  }

  // Dynamically derive legend items from the provided chart data
  /**
   * Returns legend items for the "washes by service type" chart using component data.
   * @example
   * getWashesByServiceTypeLegendItems()
   * [{ label: 'Basic', color: '#ff0000', value: 12 }, { label: 'Premium', color: '#00ff00', value: 8 }]
   * @returns {Array<{ label: string; color: string; value?: number }>} Array of legend items where each item contains a label, a color (fallback '#cccccc' if not available) and an optional numeric value.
   **/
  get washesByServiceTypeLegendItems(): { label: string; color: string; value?: number }[] {
    const labels: string[] = this.washesByServiceTypeData?.labels ?? [];
    const dataset = this.washesByServiceTypeData?.datasets?.[0] ?? {};
    const colors: string[] = dataset?.backgroundColor ?? [];
    const values: number[] = dataset?.data ?? [];

    return labels.map((label: string, index: number) => ({
      label,
      color: colors.length ? colors[index % colors.length] : '#cccccc',
      value: values[index],
    }));
  }

  // Calculate total washes from API data
  get totalWashes(): number {
    const data = this.washesByServiceTypeData?.datasets?.[0]?.data ?? [];
    return data.reduce((sum: number, value: number) => sum + value, 0);
  }

  // Check if stats data is empty
  isCarWashStatsDataEmpty(): boolean {
    return (
      !this.carWashDashboardStats ||
      this.carWashDashboardStats.length === 0 ||
      this.carWashDashboardStats.every(stat => stat.value === '0' || parseInt(stat.value, 10) === 0)
    );
  }

  // Check if all data sources are empty
  isAllDataEmpty(): boolean {
    return this.isWashesByHourDataEmpty() && this.isWashesByServiceTypeDataEmpty();
  }

  // Helper method to determine if we should show the Figma empty state
  shouldShowEmptyState(): boolean {
    return !this.isInitialLoad && !this.hasAppliedFilters && this.isAllDataEmpty();
  }

  // Helper method to determine if we should show individual chart empty states
  shouldShowIndividualEmptyStates(): boolean {
    return this.hasAppliedFilters || !this.isInitialLoad;
  }

  trackByValue(_: number, option: { value: string }): string {
    return option.value;
  }
  trackByStatTitle(_: number, stat: DashboardStatData): string {
    return stat.title;
  }
  trackByLabel(_: number, item: { label: string }): string {
    return item.label;
  }
}
